/**
 * FlahaSOIL v2 — interpretation card.
 *
 * Renders a `SoilInterpretationDTO`'s categorical fields as rows of
 * label + status chip + progressive plain-language help.
 */
import {
	Box,
	CardContent,
	CardHeader,
	Chip,
	Divider,
	Stack,
	Typography,
} from "@mui/material";
import type { SoilInterpretationDTO } from "@flaha/shared-types";

import { GlassSurface } from "../../../layouts/components/GlassSurface";
import {
	INTERPRETATION_FIELD_HELP,
	INTERPRETATION_FIELD_METHOD,
} from "../utils/agronomicCopy";
import {
	categoryToStatus,
	ratingToStatus,
} from "../utils/interpretationStatus";
import { PROVENANCE_LABELS } from "../utils/provenanceLabels";
import { FieldHelp } from "./FieldHelp";

interface InterpretationCardProps {
	interpretation: SoilInterpretationDTO | null | undefined;
}

const ROWS: Array<{ field: keyof SoilInterpretationDTO; label: string }> = [
	{ field: "phCategory", label: "pH" },
	{ field: "salinityRisk", label: "Salinity" },
	{ field: "cecLevel", label: "CEC level" },
	{ field: "baseSaturationCategory", label: "Base saturation" },
	{ field: "cationBalance", label: "Cation balance" },
	{ field: "sodiumRisk", label: "Sodium risk" },
	{ field: "waterHoldingClass", label: "Water holding" },
	{ field: "drainageClass", label: "Drainage" },
];

export function InterpretationCard({ interpretation }: InterpretationCardProps) {
	const overall = ratingToStatus(interpretation?.overallSoilRating);
	return (
		<GlassSurface
			component="section"
			aria-labelledby="interpretation-results-title"
			sx={{ height: "100%" }}
		>
			<CardHeader
				title="Interpretation"
				titleTypographyProps={{
					id: "interpretation-results-title",
					component: "h2",
					variant: "h6",
				}}
				subheader={PROVENANCE_LABELS.interpreted}
				action={
					interpretation ? (
						<Chip
							label={interpretation.overallSoilRating}
							color={overall.color}
							size="small"
						/>
					) : null
				}
			/>
			<Divider sx={{ borderColor: "workspace.border" }} />
			<CardContent>
				{!interpretation ? (
					<Typography variant="body2" color="text.secondary">
						No interpretation yet.
					</Typography>
				) : (
					<Stack spacing={1.75}>
						{ROWS.map(({ field, label }) => {
							const value = interpretation[field] as string | null | undefined;
							return (
								<InterpretationRow
									key={field}
									label={label}
									value={value ?? null}
									status={categoryToStatus(field as string, value ?? null)}
									summary={INTERPRETATION_FIELD_HELP[field as string]}
									detail={INTERPRETATION_FIELD_METHOD[field as string]}
								/>
							);
						})}
					</Stack>
				)}
			</CardContent>
		</GlassSurface>
	);
}

function InterpretationRow({
	label,
	value,
	status,
	summary,
	detail,
}: {
	label: string;
	value: string | null;
	status: ReturnType<typeof categoryToStatus>;
	summary?: string | undefined;
	detail?: string | undefined;
}) {
	return (
		<Box>
			<Stack
				direction="row"
				justifyContent="space-between"
				alignItems="center"
				spacing={1}
			>
				<Typography variant="body2" color="text.secondary">
					{label}
				</Typography>
				{value ? (
					<Chip
						label={value}
						color={status.color}
						size="small"
						variant={status.tone === "neutral" ? "outlined" : "filled"}
					/>
				) : (
					<Typography variant="body2" color="text.disabled">
						{"\u2014"}
					</Typography>
				)}
			</Stack>
			<FieldHelp summary={summary} detail={detail} />
		</Box>
	);
}
